import APIError from '~/utils/infra/buzz/error'
import {
  EVENTS,
  ERRORS,
  SOCKET_STATUS,
  MICRO_SERVICE_ERRORS,
  baseURL,
  basePath,
  observer,
} from '~/utils/infra/buzz/types'
import { normalizeRealtimeMessage } from '~/utils/infra/common/ws/message-normalizer'
import { io } from 'socket.io-client'

/**
 * @typedef {Omit<Partial<import('socket.io-client').ManagerOptions> & Partial<import('socket.io-client').SocketOptions>, 'path' | 'withCredentials'>} ConnectOptions
 */

/**
 * Thin Socket.IO bridge with Buzz defaults.
 *
 * @constructor
 */
const BuzzWSClient = function() {
  this.url = baseURL
  /** @type {ConnectOptions} */
  this.socketOptions = {}

  this.status = SOCKET_STATUS.IDLE
  /** @type {import('socket.io-client').Socket|null} */
  this.socket = null
  /** @type {Promise<void>|null} */
  this.pendingConnectPromise = null
}

export default BuzzWSClient

/**
 * @param {ConnectOptions} [options]
 * @returns {Promise<void>}
 */
BuzzWSClient.prototype.connect = function(options = {}) {
  if (this.url === '') {
    const err = new APIError(
      ERRORS.MISSING_SOCKET_URL,
      'Buzz realtime is disabled: missing websocket URL'
    )
    observer.emit(EVENTS.ON_SOCKET_ERROR, err)
    return Promise.reject(err)
  }

  const socket = ensureSocket(this, options)

  if (socket.connected)
    return Promise.resolve()

  if (this.pendingConnectPromise)
    return this.pendingConnectPromise

  setStatus(this, SOCKET_STATUS.CONNECTING)
  this.pendingConnectPromise = waitForSocketConnect(this, socket)
  socket.connect()
  return this.pendingConnectPromise
}

/**
 * @returns {Promise<void>}
 */
BuzzWSClient.prototype.disconnect = function() {
  this.pendingConnectPromise = null

  if (this.socket)
    this.socket.disconnect()

  setStatus(this, SOCKET_STATUS.CLOSED)
  return Promise.resolve()
}

/**
 * @returns {import('socket.io-client').Socket|null}
 */
BuzzWSClient.prototype.getSocket = function() {
  return this.socket
}

/**
 * @param {string} eventName
 * @param {(...args: Array<any>) => void} handler
 * @returns {() => void}
 */
BuzzWSClient.prototype.on = function(eventName, handler) {
  if (typeof handler !== 'function')
    return () => {}

  const socket = ensureSocket(this)
  socket.on(eventName, handler)

  return () => this.off(eventName, handler)
}

/**
 * @param {string} eventName
 * @param {(...args: Array<any>) => void} handler
 */
BuzzWSClient.prototype.off = function(eventName, handler) {
  if (!this.socket)
    return

  this.socket.off(eventName, handler)
}

/**
 * @param {(...args: Array<any>) => void} handler
 * @returns {() => void}
 */
BuzzWSClient.prototype.onAny = function(handler) {
  if (typeof handler !== 'function')
    return () => {}

  const socket = ensureSocket(this)
  socket.onAny(handler)
  return () => this.offAny(handler)
}

/**
 * @param {(...args: Array<any>) => void} handler
 */
BuzzWSClient.prototype.offAny = function(handler) {
  if (!this.socket)
    return

  this.socket.offAny(handler)
}

/**
 * @param {string} eventName
 * @param {*} payload
 * @returns {Promise<void>}
 */
BuzzWSClient.prototype.emit = function(eventName, payload) {
  if (!this.socket || !this.socket.connected)
    return Promise.resolve()

  this.socket.emit(eventName, payload)
  return Promise.resolve()
}

/**
 * @param {BuzzWSClient} self
 * @param {ConnectOptions} [options]
 * @returns {import('socket.io-client').Socket}
 */
function ensureSocket(self, options = {}) {
  if (self.socket)
    return self.socket

  const connectOptions = {
    ...self.socketOptions,
    ...sanitizeConnectOptions(options),
  }
  self.socketOptions = { ...connectOptions }

  const { endpointURL, socketOptions } = buildSocketConnection(self.url, connectOptions)
  self.socket = io(endpointURL, socketOptions)
  bindSocketEvents(self)
  return self.socket
}

/**
 * @param {BuzzWSClient} self
 */
function bindSocketEvents(self) {
  if (!self.socket)
    return

  self.socket.on('connect', () => onOpen(self))
  self.socket.on('disconnect', reason => onClose(self, reason))
  self.socket.on('connect_error', error => onError(self, error))
  self.socket.on('error', error => onError(self, error))
  self.socket.onAny((eventName, ...args) => onMessage(eventName, args))
}

/**
 * @param {BuzzWSClient} self
 */
function onOpen(self) {
  setStatus(self, SOCKET_STATUS.OPEN)
  observer.emit(EVENTS.ON_SOCKET_OPEN, { source: 'buzz' })
}

/**
 * @param {BuzzWSClient} self
 * @param {string} reason
 */
function onClose(self, reason) {
  setStatus(self, SOCKET_STATUS.CLOSED)
  observer.emit(EVENTS.ON_SOCKET_CLOSE, {
    source: 'buzz',
    code: reason === 'io client disconnect' ? 1000 : 1006,
    reason,
    wasClean: reason === 'io client disconnect',
  })
}

/**
 * @param {BuzzWSClient} _
 * @param {*} event
 */
function onError(_, event) {
  const err = new APIError(
    MICRO_SERVICE_ERRORS.MISC,
    'Buzz socket.io error',
    event
  )
  observer.emit(EVENTS.ON_SOCKET_ERROR, err)
}

/**
 * @param {string} eventName
 * @param {Array<any>} args
 */
function onMessage(eventName, args) {
  if (isReservedSocketEvent(eventName))
    return

  try {
    const payload = args.length <= 1 ? args[0] : args
    const envelope = normalizeRealtimeMessage(
      'buzz',
      eventName === 'message'
        ? payload
        : { type: eventName, payload }
    )
    observer.emit(EVENTS.ON_MESSAGE, envelope)
  } catch (error) {
    const err = new APIError(
      ERRORS.UNKNOWN_MESSAGE_SCHEMA,
      'Buzz socket.io message cannot be parsed',
      error
    )
    observer.emit(EVENTS.ON_SOCKET_ERROR, err)
  }
}

/**
 * @param {BuzzWSClient} self
 * @param {import('socket.io-client').Socket} socket
 * @returns {Promise<void>}
 */
function waitForSocketConnect(self, socket) {
  if (socket.connected)
    return Promise.resolve()

  return new Promise((resolve, reject) => {
    const cleanup = () => {
      socket.off('connect', onConnect)
      socket.off('connect_error', onConnectError)
      socket.off('error', onConnectError)
      socket.off('disconnect', onDisconnect)
    }

    const finalize = () => {
      cleanup()
      self.pendingConnectPromise = null
    }

    const onConnect = () => {
      finalize()
      resolve()
    }

    /**
     * @param {*} error
     */
    const onConnectError = (error) => {
      finalize()
      reject(toConnectionError(error))
    }

    /**
     * @param {string} reason
     */
    const onDisconnect = (reason) => {
      finalize()
      reject(new Error(`buzz socket disconnected before connect: ${reason}`))
    }

    socket.on('connect', onConnect)
    socket.on('connect_error', onConnectError)
    socket.on('error', onConnectError)
    socket.on('disconnect', onDisconnect)
  })
}

/**
 * @param {*} error
 * @returns {Error}
 */
function toConnectionError(error) {
  if (error instanceof Error)
    return error

  return new Error('buzz socket connection failed')
}

/**
 * @param {BuzzWSClient} self
 * @param {typeof SOCKET_STATUS[keyof typeof SOCKET_STATUS]} status
 */
function setStatus(self, status) {
  self.status = status
  observer.emit(EVENTS.ON_SOCKET_STATUS_CHANGE, { source: 'buzz', status })
}

/**
 * @param {string} eventName
 * @returns {boolean}
 */
function isReservedSocketEvent(eventName) {
  return eventName === 'connect'
    || eventName === 'disconnect'
    || eventName === 'connect_error'
    || eventName === 'error'
}

/**
 * @param {string} url
 * @param {ConnectOptions} options
 * @returns {{ endpointURL: string; socketOptions: Record<string, any> }}
 */
function buildSocketConnection(url, options) {
  let endpointURL = url

  try {
    const parsed = new URL(url)
    const protocol = parsed.protocol === 'ws:'
      ? 'http:'
      : parsed.protocol === 'wss:'
        ? 'https:'
        : parsed.protocol
    endpointURL = `${protocol}//${parsed.host}`
  } catch (_) {
    endpointURL = url
  }

  const socketPath = normalizeSocketPath(basePath)

  /** @type {Partial<import('socket.io-client').ManagerOptions & import('socket.io-client').SocketOptions>} */
  const socketOptions = {
    ...options,
    autoConnect: false,
    reconnection: true,
    transports: [ 'websocket' ]
  }
  socketOptions.withCredentials = true
  socketOptions.path = socketPath

  return { endpointURL, socketOptions }
}

/**
 * @param {ConnectOptions} options
 * @returns {ConnectOptions}
 */
function sanitizeConnectOptions(options) {
  const {
    path: _ignoredPath,
    withCredentials: _ignoredWithCredentials,
    ...allowed
  } = /** @type {Record<string, any>} */ (options || {})

  return /** @type {ConnectOptions} */ (allowed)
}

/**
 * @param {string} path
 * @returns {string}
 */
function normalizeSocketPath(path) {
  if (path.startsWith('/'))
    return path

  return `/${path}`
}

